import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Schedule from './pages/Schedule';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import Admin from './pages/Admin';
import Dashboard from './pages/admin/Dashboard';
import ClientManagement from './pages/admin/ClientManagement';
import ProductManagement from './pages/admin/ProductManagement';
import BlogEditor from './pages/admin/BlogEditor';
import EditBlog from './pages/admin/EditBlog';
import ManageGallery from './pages/admin/ManageGallery';
import ProtectedRoute from './components/ProtectedRoute';
import TrainingManagement from './pages/admin/TrainingManagement';

const theme = createTheme({
  palette: {
    primary: {
      main: '#755985',
      light: '#a486b4',
      dark: '#5b4468',
    },
    secondary: {
      main: '#f50057',
      light: '#ff5983',
      dark: '#bb002f',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '3.5rem', fontWeight: 700 },
    h2: { fontSize: '2.5rem', fontWeight: 600 },
  },
});

// Component to conditionally render navbar and footer
const Layout = ({ children, hideNav = false }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    {!hideNav && <Navbar />}
    <Box component="main" sx={{ flexGrow: 1 }}>
      {children}
    </Box>
    {!hideNav && <Footer />}
  </Box>
);

const App = () => {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/schedule" element={<Layout><Schedule /></Layout>} />
            <Route path="/blog" element={<Layout><Blog /></Layout>} />
            <Route path="/blog/:id" element={<Layout><BlogPost /></Layout>} />
            <Route path="/gallery" element={<Layout><Gallery /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/admin" element={<Layout hideNav><Admin /></Layout>} />

            {/* Protected Admin Routes */}
            <Route path="/admin/dashboard" element={
              <ProtectedRoute>
                <Layout hideNav>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/clients" element={
              <ProtectedRoute>
                <Layout hideNav>
                  <ClientManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/products" element={
              <ProtectedRoute>
                <Layout hideNav>
                  <ProductManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog/new" element={
              <ProtectedRoute>
                <Layout hideNav>
                  <BlogEditor />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog/edit/:id" element={
              <ProtectedRoute>
                <Layout hideNav>
                  <EditBlog />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/gallery/new" element={
              <ProtectedRoute>
                <Layout hideNav>
                  <ManageGallery />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/gallery" element={
              <ProtectedRoute>
                <Layout hideNav>
                  <ManageGallery />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/training" element={
              <ProtectedRoute>
                <Layout hideNav>
                  <TrainingManagement />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
